import { isLngLat } from "../shared/geo.ts";
import { graphFromLine, sanitizeGraph } from "../shared/route-graph.ts";
import type { LngLat, PieceGraph } from "../shared/types.ts";

const KEY = "mapus.editor-draft.v1";
const LEGACY_KEY = "mapsterpiece.editor-draft.v1";

export interface EditorDraft {
  title: string;
  graph: PieceGraph;
}

export function loadDraft(): EditorDraft | null {
  try {
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      title?: unknown;
      graph?: { points?: unknown; segments?: unknown; start?: unknown };
      points?: unknown;
      manualClosed?: unknown;
    };
    const title = typeof parsed.title === "string" ? parsed.title.slice(0, 80) : "";
    const graph = readGraph(parsed) ?? readLegacyLine(parsed);
    if (!graph) return null;
    if (graph.points.length === 0 && !title && !graph.workArea) return null;
    return { title, graph };
  } catch {
    return null;
  }
}

export function saveDraft(draft: EditorDraft): void {
  try {
    if (draft.graph.points.length === 0 && !draft.title.trim() && !draft.graph.workArea) {
      localStorage.removeItem(KEY);
      localStorage.removeItem(LEGACY_KEY);
      return;
    }
    localStorage.setItem(KEY, JSON.stringify(draft));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* private mode or a full disk — the piece still lives in the session */
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

function readGraph(parsed: {
  graph?: {
    points?: unknown;
    segments?: unknown;
    start?: unknown;
    workArea?: unknown;
    regions?: unknown;
    pointStyles?: unknown;
    lineStyles?: unknown;
    decorative?: unknown;
    styleDefaults?: unknown;
  };
}): PieceGraph | null {
  const graph = parsed.graph;
  if (!graph || !Array.isArray(graph.points) || !graph.points.every(isLngLat)) return null;
  if (!Array.isArray(graph.segments)) return null;
  const segments: Array<[number, number]> = [];
  for (const seg of graph.segments) {
    if (!Array.isArray(seg) || seg.length < 2) return null;
    const a = seg[0];
    const b = seg[1];
    if (typeof a !== "number" || typeof b !== "number") return null;
    segments.push([a, b]);
  }
  const start = typeof graph.start === "number" ? graph.start : null;
  return sanitizeGraph({
    points: graph.points.map((p) => [p[0], p[1]] as LngLat),
    segments,
    start,
    workArea: graph.workArea,
    regions: graph.regions,
    pointStyles: graph.pointStyles,
    lineStyles: graph.lineStyles,
    decorative: graph.decorative,
    styleDefaults: graph.styleDefaults,
  });
}

function readLegacyLine(parsed: { points?: unknown; manualClosed?: unknown }): PieceGraph | null {
  if (!Array.isArray(parsed.points) || !parsed.points.every(isLngLat)) return null;
  if (parsed.points.length === 0) return null;
  return graphFromLine(
    parsed.points.map((p) => [p[0], p[1]] as LngLat),
    parsed.manualClosed === true,
  );
}
